import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Fragment, useEffect } from 'react';
import { CommonLink } from '../../common/CommonLink';
import { twMerge } from 'tailwind-merge';
import { HoldingResponse } from '../../../../server/response/responseScheme';
import currencyStore from '../../../stores/currencyStore';
import Table from '../../../uikit/Table';
import { apiClient } from '../../../clientUtils/apiClient';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';
import stringUtil from '../../../clientUtils/stringUtil';
import StockLogo from '../../common/StockLogo';
import { ChevronRightIcon } from 'lucide-react';
import SearchInput, { Searchable, SearchNoResult } from '../../common/SearchInput';

class ViewStore {
    readonly institutionSlug: string;
    readonly reportYear: number;
    readonly reportQuarter: number;

    holdingList: HoldingResponse[] = [];
    selectedHolding: HoldingResponse | undefined;

    searchable: Searchable<HoldingResponse>;

    constructor(institutionSlug: string, reportYear: number, reportQuarter: number) {
        this.institutionSlug = institutionSlug;
        this.reportYear = reportYear;
        this.reportQuarter = reportQuarter;

        this.searchable = new Searchable<HoldingResponse>({
            fields: [
                'security.name',
                'security.stock.name',
                'security.stock.nameKo',
                'security.stock.ticker',
            ],
            searchOptions: {
                boost: {
                    'security.stock.ticker': 3,
                },
            },
        });

        makeAutoObservable(this);
    }

    async fetch() {
        if (!this.institutionSlug) {
            return;
        }

        const [holdingList] = await Promise.all([
            apiClient.report.public.getHoldingList.$query({
                institutionSlug: this.institutionSlug,
                reportYear: this.reportYear,
                reportQuarter: this.reportQuarter,
            }),
        ]);

        runInAction(() => {
            this.holdingList = holdingList;
            this.searchable.setItems(holdingList);
        });
    }

    selectHolding(holding: HoldingResponse | undefined) {
        this.selectedHolding = holding;
    }
}

const HoldingListTable = observer(
    ({
        institutionSlug,
        reportYear,
        reportQuarter,
    }: {
        institutionSlug: string;
        reportYear: number;
        reportQuarter: number;
    }) => {
        const viewStore = useMobxInstance(
            ViewStore,
            institutionSlug as string,
            Number(reportYear),
            Number(reportQuarter),
        );

        useEffect(() => {
            viewStore.fetch();
        }, [viewStore]);

        return (
            <div className="flex min-h-dvh flex-col gap-3">
                <SearchInput searchable={viewStore.searchable} />
                {viewStore.searchable && _.isEmpty(viewStore.searchable.searchedItems) && (
                    <SearchNoResult />
                )}
                {viewStore.searchable && !_.isEmpty(viewStore.searchable.searchedItems) && (
                    <Table fixed={true}>
                        <Table.Header>종목명</Table.Header>
                        <Table.Header className="w-[80px]">비중</Table.Header>
                        <Table.Header className="w-[90px]">가치</Table.Header>

                        {_.map(viewStore.searchable.searchedItems, (holding) => {
                            const percentage = stringUtil.toPercentage(holding.weight);
                            const value = currencyStore.toCompactUsdOrKrw(
                                Number(holding.value) * 1000,
                            );
                            const isSelected = viewStore.selectedHolding?.hash === holding.hash;

                            const securityName =
                                holding.security.stock?.nameKo ||
                                holding.security.stock?.name ||
                                holding.security.name;

                            const ticker = holding.security.stock?.ticker;

                            return (
                                <Fragment key={holding.hash}>
                                    <Table.Row
                                        className={twMerge(
                                            'cursor-pointer',
                                            viewStore.selectedHolding?.hash === holding.hash &&
                                                'bg-primary/5',
                                        )}
                                        onClick={() => {
                                            if (isSelected) {
                                                viewStore.selectHolding(undefined);
                                            } else {
                                                viewStore.selectHolding(holding);
                                            }
                                        }}
                                    >
                                        <Table.Cell className="flex items-start gap-1.5">
                                            <StockLogo
                                                size={6}
                                                stock={holding.security.stock}
                                                altName={securityName}
                                            />
                                            <div className="truncate">{securityName}</div>
                                        </Table.Cell>
                                        <Table.Cell className="tabular-nums">
                                            {percentage}
                                        </Table.Cell>
                                        <Table.Cell className="tabular-nums">{value}</Table.Cell>
                                    </Table.Row>
                                    {isSelected && (
                                        <Table.Row className={twMerge('bg-primary/5')}>
                                            <Table.Cell colSpan={3}>
                                                <div className="flex flex-row items-start justify-start gap-2 py-4 pr-2 pl-4">
                                                    <div className="flex grow flex-col gap-2">
                                                        {ticker && (
                                                            <div className="rounded-sm text-base font-bold">
                                                                {ticker}
                                                            </div>
                                                        )}
                                                        <div className="text-base-content/70 text-sm text-wrap">
                                                            {holding.security.name}
                                                        </div>
                                                    </div>
                                                    {viewStore.selectedHolding?.security?.hash && (
                                                        <CommonLink
                                                            to={`/portfolio/${viewStore.institutionSlug}/security/${viewStore.selectedHolding?.security?.hash}`}
                                                            className="btn btn-xs btn-soft btn-primary shrink-0 justify-between gap-0.5"
                                                        >
                                                            자세히
                                                            <ChevronRightIcon className="size-3" />
                                                        </CommonLink>
                                                    )}
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Fragment>
                            );
                        })}
                    </Table>
                )}
            </div>
        );
    },
);

export default HoldingListTable;
